import { Location } from '@angular/common';
import { Component, OnInit, ViewChild, OnDestroy, Renderer2, ElementRef, ChangeDetectionStrategy, NgZone, ChangeDetectorRef } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { User } from '../../models';
import { UtilsService } from '../../services';
import { NotificationService } from '../../services/notification.service';
import _ from 'lodash';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { I } from '@angular/cdk/keycodes';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'a8-nav',
    templateUrl: './nav.component.html',
    styleUrls: ['./nav.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NavComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];
  url: string;
  previousUrl: string;
  profileid: string;
  flags = {
    sidenav: false,
    onProfile: false,
    animate_icon: false,
    showMenu: null,
    userImg: 'none',
  };
  version = environment.VERSION;
  user: User = null;
  pendingInvitesCount: number = 0;
  notificationsLimit: number = 100;
  currentPortfolio: any;
  navComm: any;
  commUrl: any;
  centerNavMode: 'plus' | 'logo' = 'logo';
  private lastLogoClickTime: number = 0;
  private logoClickTimeout: any = null;
  private logoHoldTimeout: any = null;
  private logoLongPressed: boolean = false;

  isNavHidden: boolean = false;
  private lastScrollY: number = 0;
  private readonly SCROLL_THRESHOLD = 8;
  private onScrollHandler: (() => void) | null = null;

  get currentCenterNavMode(): 'plus' | 'logo' {
    return 'logo';
  }

  getPendingInvitesCount(): number {
    if (this.pendingInvitesCount > 0) {
      return this.pendingInvitesCount;
    }
    return (this.user?.metrics?.unread_invites || 0);
  }

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  // @ViewChild('messageDiv') messageDiv : ElementRef<HTMLInputElement>;
  @ViewChild('messageDiv', {static: false}) messageDiv: ElementRef;
  @ViewChild('messageText', {static: false}) messageText: ElementRef;
  @ViewChild('navtoolbar', {static: false}) navtoolbar: ElementRef;
  
  openMenu() {
    this.trigger.openMenu();
  }

  constructor(
    public utils: UtilsService,
    private notificationService: NotificationService,
    public router: Router,
    private location: Location,
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    this.route.params.subscribe(
      (params) => (this.profileid = params['username'])
    );

    this.utils.appState.sidenav.subscribe((resp) => {
      this.flags.sidenav = resp;
      this.toggleAnimate();
      if (resp && this.isNavHidden) {
        this.isNavHidden = false;
        this.cdr.markForCheck();
      }
    });

    // NOT updated before view loads?
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.url;
        this.url = event.url;

        this.flags.onProfile = false;
        let urlParts = this.url.split('/');
        if (urlParts[1] == 'profile' && urlParts.length == 3) {
          this.flags.onProfile = true;
        }

        if (this.isNavHidden) {
          this.isNavHidden = false;
          this.cdr.markForCheck();
        }
        this.lastScrollY = 0;
      }
    });

    this.utils.appState.user.subscribe((resp) => {
      this.user = resp;
      this.flags.userImg = this.getProfileBackground(this.user);
    });

    this.utils.appState.invites.subscribe((invites: any[]) => {
      if (invites && Array.isArray(invites) && this.user?.id) {
        this.pendingInvitesCount = invites.filter((inv: any) => {
          const isPending = inv.status === 'pending' || inv.status === 'paid-cancel';
          const isForMe = inv.to?.id === this.user.id || (inv.to && String(inv.to.id) === String(this.user.id));
          const isUpcoming = !inv.datetime || moment(inv.datetime).isSameOrAfter(moment());
          return isPending && isForMe && isUpcoming;
        }).length;
      }
    });


    this.utils.appState.navComm.subscribe((resp) => {
      if (!resp || !resp.comm || !resp.comm.length) return;

      this.utils.delay(10000).then(() => {
        if (!this.messageDiv || !this.messageText) return;

        this.renderer.addClass(this.messageDiv.nativeElement, 'growNav');
        this.utils.delay(300).then(() => {
          if (this.messageText) {
            this.renderer.addClass(this.messageText.nativeElement, 'textNotice');
          }
        });

        this.navComm = resp.comm[0];
        this.commUrl = resp.url;
        let delay = 5000;
        let nextDelay = 0;

        // HIDE NAVCOMM AFTER
        this.utils.delay(delay * (resp.comm.length + 4)).then(() => {
          this.utils.delay(400).then(() => {
            this.hideComm();
          });
        });

        resp.comm.forEach((comm: any) => {
          nextDelay = nextDelay + delay;
          if (resp['time'] != null) {
            delay = resp['time'];
          }
          if (this.messageText != null) {
            this.utils.delay(nextDelay).then(() => {
              if (this.messageText) {
                this.renderer.removeClass(this.messageText.nativeElement, 'textNotice');
                this.utils.delay(100).then(() => {
                  if (this.messageText) {
                    this.renderer.addClass(this.messageText.nativeElement, 'textNotice');
                  }
                });
                this.navComm = comm;
              }
            });
          }
        });
      });
    });
  }

  hideComm(){
    this.renderer.removeClass(this.messageText.nativeElement, 'textNotice');
    this.renderer.removeClass(this.messageDiv.nativeElement, 'growNav');
    this.renderer.addClass(this.messageDiv.nativeElement, 'shrinkNav');
  }

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      this.onScrollHandler = () => {
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const maxScrollY = (document.documentElement.scrollHeight || 0) - window.innerHeight;

        // At or near top, always keep visible (200px or 20% of viewport, whichever is larger)
        const topBoundary = Math.max(200, (window.innerHeight || 0) * 0.2);
        if (currentScrollY <= topBoundary) {
          if (this.isNavHidden) {
            this.ngZone.run(() => {
              this.isNavHidden = false;
              this.cdr.markForCheck();
            });
          }
          this.lastScrollY = Math.max(0, currentScrollY);
          return;
        }

        // Ignore elastic bounce past top or bottom on mobile
        if (currentScrollY < 0 || (maxScrollY > 0 && currentScrollY > maxScrollY)) {
          return;
        }

        const delta = currentScrollY - this.lastScrollY;
        if (Math.abs(delta) < this.SCROLL_THRESHOLD) {
          return;
        }

        if (delta > 0 && !this.isNavHidden) {
          // Scrolling down -> hide
          this.ngZone.run(() => {
            this.isNavHidden = true;
            this.cdr.markForCheck();
          });
        } else if (delta < 0 && this.isNavHidden) {
          // Scrolling up -> show
          this.ngZone.run(() => {
            this.isNavHidden = false;
            this.cdr.markForCheck();
          });
        }

        this.lastScrollY = currentScrollY;
      };

      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    });
  }

  capacitor(){
    return Capacitor.isNativePlatform();
  }

  closeMenu(){
    if(this.flags.showMenu){
      this.flags.showMenu = this.flags.showMenu=false;
    }
    if (this.isNavHidden) {
      this.isNavHidden = false;
      this.cdr.markForCheck();
    }
  }

  delayAction(event, action, animation, delay?, activity?){
    if(delay == null){
      delay = 800;
    }
    // this.flags.clicked = true;
    this.renderer.addClass(event.currentTarget, animation);
    this.renderer.addClass(event.currentTarget, 'on');
    let item = event.currentTarget;

    this.utils.delay(delay).then(() => {
      this.renderer.removeClass(item, animation);
      this.renderer.removeClass(item, 'on');
      this.showPostBox('planning');
    });
  }


  private lastProfileClickTime: number = 0;
  private profileClickTimeout: any = null;

  onProfileClick(event?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const now = Date.now();
    const timeDiff = now - this.lastProfileClickTime;

    if (timeDiff < 350 && timeDiff > 0) {
      // Double click / tap detected -> navigate to Recent
      if (this.profileClickTimeout) {
        clearTimeout(this.profileClickTimeout);
        this.profileClickTimeout = null;
      }
      this.lastProfileClickTime = 0;
      this.closeMenu();
      this.utils.sound('toggle');
      window.scrollTo(0, 0);
      this.router.navigateByUrl('/recent');
    } else {
      // First click: wait for potential second click
      this.lastProfileClickTime = now;
      if (this.profileClickTimeout) {
        clearTimeout(this.profileClickTimeout);
      }
      this.profileClickTimeout = setTimeout(() => {
        this.profileClickTimeout = null;
        this.closeMenu();
        this.utils.sound('click-face');
        window.scrollTo(0, 0);
        if (this.user?.username) {
          this.router.navigateByUrl('/' + this.user.username);
        }
      }, 300);
    }
  }

  onLogoPointerDown(event: any) {
    this.logoLongPressed = false;
    if (this.logoHoldTimeout) {
      clearTimeout(this.logoHoldTimeout);
    }
    this.logoHoldTimeout = setTimeout(() => {
      this.logoLongPressed = true;
      this.clickCreatePost(event);
    }, 500);
  }

  onLogoPointerUp(event?: any) {
    if (this.logoHoldTimeout) {
      clearTimeout(this.logoHoldTimeout);
      this.logoHoldTimeout = null;
    }
  }

  onLogoPointerLeave(event?: any) {
    if (this.logoHoldTimeout) {
      clearTimeout(this.logoHoldTimeout);
      this.logoHoldTimeout = null;
    }
  }

  onLogoDblClick(event: any) {
    if (this.logoClickTimeout) {
      clearTimeout(this.logoClickTimeout);
      this.logoClickTimeout = null;
    }
    this.lastLogoClickTime = 0;
    this.clickCreatePost(event);
  }

  onLogoClick(event: any) {
    if (this.logoLongPressed) {
      this.logoLongPressed = false;
      return;
    }
    const now = Date.now();
    const diff = now - this.lastLogoClickTime;
    if (diff < 350 && diff > 0) {
      if (this.logoClickTimeout) {
        clearTimeout(this.logoClickTimeout);
        this.logoClickTimeout = null;
      }
      this.lastLogoClickTime = 0;
      this.clickCreatePost(event);
    } else {
      this.lastLogoClickTime = now;
      if (this.logoClickTimeout) {
        clearTimeout(this.logoClickTimeout);
      }
      this.logoClickTimeout = setTimeout(() => {
        this.logoClickTimeout = null;
        this.clickLogo(event);
      }, 300);
    }
  }

  clickLogo(event?: any) {
    const current = this.router.url || this.url || '';
    if (current === '/dashboard' || current === '/dashboard/' || current.startsWith('/dashboard?') || current.includes('/discover')) {
      this.clickCreatePost(event);
      return;
    }
    this.closeMenu();
    this.utils.sound();
    window.scrollTo(0, 0);
    this.router.navigateByUrl('/dashboard');
  }

  toggleCenterNavMode() {
    const next = this.currentCenterNavMode === 'logo' ? 'logo' : 'plus';
    this.centerNavMode = next;
    if (!this.user) {
      this.user = {} as any;
    }
    if (!this.user.app_state) {
      this.user.app_state = {};
    }
    this.user.app_state['centerNavMode'] = next;
    this.utils.appState.user.next(this.user);
  }

  clickCenterAction(event: any) {
    if (this.currentCenterNavMode === 'plus') {
      this.clickCreatePost(event);
    } else {
      this.clickLogo(event);
    }
  }

  clickCreatePost(event?: any) {
    this.utils.sound('toggle');
    this.closeMenu();
    if (event?.currentTarget) {
      this.renderer.addClass(event.currentTarget, 'on');
    }
    this.utils.delay(200).then(() => {
      if (event?.currentTarget) {
        this.renderer.removeClass(event.currentTarget, 'on');
      }
    });
    if (!this.user) {
      this.user = {} as any;
    }
    if (!this.user.app_state) {
      this.user.app_state = {};
    }
    const currentBox = this.user.app_state['createPostBox'];
    if (this.router.url === '/dashboard' && currentBox === 'planning') {
      this.user.app_state['createPostBox'] = 'outLeft';
    } else {
      this.user.app_state['createPostBox'] = 'planning';
    }
    this.utils.appState.user.next(this.user);
    if (this.router.url !== '/dashboard') {
      this.router.navigateByUrl('/dashboard');
    }
  }

  toggleMenu(){
    if(this.flags.showMenu == null){
      this.flags.showMenu = true;
    }
    else{
      this.flags.showMenu = !this.flags.showMenu;
    }
  }

  showPostBox(type?) {
    this.utils.sound();
    this.user.app_state['createPostBox'] = type;
    this.utils.appState.user.next(this.user);
    this.router.navigateByUrl('/dashboard');
  }  

  toggleAnimate(){
    this.flags.animate_icon = !this.flags.animate_icon;
  }

  clickLogin() {
    this.router.navigateByUrl('/login?returnUrl=' + this.router.url);
  }

  showHome(){
    if(this.previousUrl != null && this.router.url != '/dashboard' && this.previousUrl.includes('returnUrl') 
    || this.previousUrl != null && this.flags?.onProfile && !this.previousUrl.includes('/invites') && !this.previousUrl.includes('/dashboard/invites')){
      return true;
    }

    else{
      return false;
    }
  }

  isNavVisible(): boolean {
    if (!this.user || !this.user.isAuthenticated) {
      return false;
    }
    if (this.url?.includes('messages/') || this.url?.includes('/about') || this.url === '/' || this.url === '/start') {
      return false;
    }
    if (this.user?.app_state?.createPostBox !== 'outLeft' || this.user?.app_state?.isMapFullscreen) {
      return false;
    }
    if (this.user?.app_state?.showSetActivities) {
      return false;
    }
    if (this.isOnboarding()) {
      return false;
    }
    return true;
  }

  isOnboarding(): boolean {
    if (!this.user) {
      return false;
    }
    if (this.user?.app_state?.showSetActivities) {
      return true;
    }
    const currentUrl = this.url || '';
    if (currentUrl === '/dashboard' || currentUrl === '/dashboard/' || currentUrl.startsWith('/dashboard?')) {
      if (this.user.color == null) {
        return true;
      }
      if (this.user.settings == null || this.user.settings.web_notifications == null) {
        return true;
      }
      if (this.user.location == null) {
        return true;
      }
      if (this.user.agree_terms == null) {
        return true;
      }
    }
    return false;
  }

  logout() {
    if (confirm('Logout?')) {
      this.router.navigate(['/logout']);
    }
  }

  getProfileBackground(profile){
    if(profile != null && profile.profile_pics != null && profile.profile_pics.photos && profile.profile_pics.photos.length > 0){
      return 'url('+ profile.profile_pics.photos[profile.profile_pics.photos.length-1].image.sm +')';
    }
    else{
      return 'none';
    }  
  }

  back() {
    let urlParts = this.url.split('/');
    if (urlParts != null && urlParts[1] == 'profile' && urlParts[2] == null || urlParts.includes('settings')) {
      this.router.navigateByUrl('/dashboard');
    } else {
      this.location.back();
    }
    // this.location.replaceState(this.url);
    // this.location.replaceState(this.previousUrl);
  }

  toggleSidenav(): void {
    this.flags.sidenav = !this.flags.sidenav;
    this.utils.appState.sidenav.next(this.flags.sidenav);
  }

  ngOnDestroy() {
    if (this.profileClickTimeout) {
      clearTimeout(this.profileClickTimeout);
      this.profileClickTimeout = null;
    }
    if (this.logoClickTimeout) {
      clearTimeout(this.logoClickTimeout);
      this.logoClickTimeout = null;
    }
    if (this.logoHoldTimeout) {
      clearTimeout(this.logoHoldTimeout);
      this.logoHoldTimeout = null;
    }
    if (this.onScrollHandler) {
      window.removeEventListener('scroll', this.onScrollHandler);
      this.onScrollHandler = null;
    }
    _.each(this.subs, (sub) => {
      sub.unsubscribe();
    });
  }

}
